import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { CheckCircle, Gift, X as XIcon } from 'lucide-react';

const FEEDBACK_REASONS = [
  "I wasn't using my membership enough",
  "I wasn't happy with the products or fit",
  "It didn't feel worth the cost",
  'Something else (Please specify)',
] as const;

// -- Animations --

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

// -- Styled Components --

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #f7f7f7;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;

  @media (min-width: 768px) {
    border-radius: 20px;
  }
`;

const SheetContent = styled.div`
  padding: 80px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  @media (max-width: 480px) {
    padding: 40px 20px;
  }
`;

const BannerCard = styled.div`
  background: #e0efe7;
  border-radius: 14px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  width: 100%;
`;

const SuccessIconGroup = styled.div`
  position: relative;
  width: 106px;
  height: 106px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GiftCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fdf1ef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f7a08b;
`;

const CheckBadge = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4cbea4;
`;

const BannerHeading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.33;
  color: #323232;
  margin: 0;
`;

const BannerDescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BannerDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #5a5a5a;
  margin: 0;
`;

const FeedbackSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 31px;
  width: 100%;
`;

const HeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  width: 100%;
`;

const Heading = styled.h2`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.56;
  color: #292929;
  margin: 0;
`;

const Subheading = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #292929;
  margin: 0;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const RadioCard = styled.label<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border: 1px solid ${({ selected }) => (selected ? '#292929' : '#e5e5e5')};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: #292929;
  }
`;

const RadioText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: #292929;
  flex: 1;
`;

const RadioCircle = styled.span<{ selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? '#292929' : '#e5e5e5')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ selected }) => (selected ? '#292929' : 'transparent')};
  }
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const CommentsTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  border: 1px solid #bbbbbb;
  border-radius: 8px;
  padding: 13px 16px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  color: #292929;
  background: #fff;

  &::placeholder {
    color: #808080;
  }

  &:focus {
    border-color: #292929;
  }
`;

const ButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 8px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  background: #f7a08b;
  border: none;
  color: #292929;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReturnButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #292929;
  cursor: pointer;
  text-align: center;
  background: none;
  border: none;

  &:hover {
    opacity: 0.7;
  }
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-top: 1px solid #eee;
`;

const LogoText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #292929;
`;

const BottomCloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

// -- Component --

export interface ConfirmationBanner {
  readonly heading: string;
  readonly description: string;
  readonly pricingNote?: string;
}

interface RetentionFeedbackPageProps {
  readonly onSubmit: (reason: string, comments: string) => void;
  readonly onReturn: () => void;
  readonly confirmationBanner?: ConfirmationBanner;
  readonly useSheetLayout?: boolean;
}

export function RetentionFeedbackPage({
  onSubmit,
  onReturn,
  confirmationBanner,
  useSheetLayout = false,
}: RetentionFeedbackPageProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comments, setComments] = useState('');

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onReturn();
    }
  };

  const content = (
    <>
      {confirmationBanner && (
        <BannerCard data-testid="success-banner">
          <SuccessIconGroup>
            <GiftCircle>
              <Gift size={32} />
            </GiftCircle>
            <CheckBadge>
              <CheckCircle size={24} />
            </CheckBadge>
          </SuccessIconGroup>
          <BannerHeading>{confirmationBanner.heading}</BannerHeading>
          <BannerDescriptionBlock>
            <BannerDescription>{confirmationBanner.description}</BannerDescription>
            {confirmationBanner.pricingNote && (
              <BannerDescription>{confirmationBanner.pricingNote}</BannerDescription>
            )}
          </BannerDescriptionBlock>
        </BannerCard>
      )}

      <FeedbackSection>
        <HeadingBlock>
          <Heading>We&rsquo;d love your feedback.</Heading>
          <Subheading>Let us know what didn&rsquo;t work for you. It helps us improve.</Subheading>
        </HeadingBlock>

        <RadioGroup role="radiogroup" aria-label="Feedback reason">
          {FEEDBACK_REASONS.map((reason) => (
            <RadioCard key={reason} selected={selectedReason === reason}>
              <HiddenRadio
                type="radio"
                name="feedback-reason"
                value={reason}
                checked={selectedReason === reason}
                onChange={() => setSelectedReason(reason)}
              />
              <RadioText>{reason}</RadioText>
              <RadioCircle selected={selectedReason === reason} />
            </RadioCard>
          ))}
        </RadioGroup>

        <CommentsTextArea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Your comments"
          aria-label="Your comments"
        />
      </FeedbackSection>

      <ButtonColumn>
        <SubmitButton
          type="button"
          onClick={() => onSubmit(selectedReason ?? '', comments)}
          disabled={!selectedReason}
          data-testid="submit-feedback-btn"
        >
          Submit
        </SubmitButton>
        <ReturnButton type="button" onClick={onReturn}>
          Return to My Account
        </ReturnButton>
      </ButtonColumn>
    </>
  );

  if (useSheetLayout) {
    return (
      <Backdrop onClick={handleBackdropClick} data-testid="retention-feedback-backdrop">
        <Sheet role="dialog" aria-modal="true" aria-label="Retention feedback">
          <SheetContent>{content}</SheetContent>
          <BottomBar>
            <BottomCloseButton onClick={onReturn} aria-label="Close">
              <XIcon size={24} />
            </BottomCloseButton>
            <LogoText>{/* Plan name or brand placeholder */}</LogoText>
            <div style={{ width: 24 }} />
          </BottomBar>
        </Sheet>
      </Backdrop>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
      data-testid="retention-feedback-page"
    >
      {content}
    </div>
  );
}
